import { z } from "zod";
import { businessMetadataShape } from "./business-metadata";

export const languageSchema = z.string().trim().toLowerCase().regex(/^[a-z]{2,3}$/);
export const localizedSchema = z.record(z.string().regex(/^[a-z]{2,3}$/), z.string())
  .refine((value) => Object.keys(value).length > 0, "At least one localized string is required.");
const localized = localizedSchema;
const entityStatus = z.enum(["active", "inactive"]);
const onboardingStatus = z.enum(["analyzing", "ready", "not_relevant", "failed"]);

export const brandingSchema = z.object({
  primaryColor: z.string(), secondaryColor: z.string(), fontFamily: z.string(), cornerRadius: z.string(), buttonStyle: z.string(), heroStyle: z.string(),
}).strict();

export const businessProfileSchema = z.object({
  ...businessMetadataShape,
  primaryLanguage: languageSchema.optional().catch(undefined),
  name: localized,
  description: localized,
  website: z.string().url(),
  vertical: localized,
  contact: z.object({ email: z.string(), phone: z.string() }).strict(),
  serviceAreas: z.array(localized),
  currency: z.string().length(3),
  timezone: z.string().min(1),
  branding: brandingSchema,
  publicSlug: z.string().min(1),
  defaultScenarioId: z.string().min(1),
  updatedAt: z.date(),
}).strict();

export const pricingFactorSchema = z.object({
  id: z.string().min(1), label: localized, kind: z.enum(["fixed", "per-unit", "multiplier"]), amount: z.number().optional(), low: z.number().optional(), high: z.number().optional(), multiplier: z.number().optional(), answerId: z.string().optional(), answerValues: z.array(z.union([z.string(), z.number()])).optional(), conditionAnswerId: z.string().min(1).optional(), conditionAnswerValues: z.array(z.union([z.string(), z.number()])).optional(),
}).strict();

const numericEmbeddedPricingSchema = z.object({
  currency: z.string().length(3), display: z.enum(["range", "from", "fixed", "hidden"]), sourceType: z.enum(["company_website", "market_research", "estimated"]), confidence: z.enum(["high", "medium", "low"]), low: z.number().nonnegative(), high: z.number().nonnegative(), fixed: z.number().nonnegative().optional(), factors: z.array(pricingFactorSchema), conditions: z.array(localized), exceptions: z.array(localized), notes: localized,
}).strict().superRefine((pricing, context) => {
  if (pricing.high < pricing.low) context.addIssue({ code: "custom", message: "Pricing high must be at least pricing low." });
  if (pricing.display === "fixed" && pricing.fixed === undefined) context.addIssue({ code: "custom", message: "Fixed pricing requires a fixed amount." });
});

export const embeddedPricingSchema = z.union([numericEmbeddedPricingSchema, z.object({
  availability: z.literal("quote-required"), currency: z.string().length(3),
  factors: z.array(pricingFactorSchema).max(0), display: z.never().optional(), low: z.never().optional(), high: z.never().optional(), fixed: z.never().optional(), sourceType: z.enum(["company_website", "market_research", "estimated"]),
  confidence: z.enum(["high", "medium", "low"]), conditions: z.array(localized),
  exceptions: z.array(localized), notes: localized,
}).strict()]);

export const productSchema = z.object({
  id: z.string().min(1), name: localized, description: localized, status: entityStatus, sourceIds: z.array(z.string()), sortOrder: z.number().int(), pricing: embeddedPricingSchema,
}).strict();

export const knowledgeSchema = z.object({
  id: z.string().min(1), type: z.enum(["faq", "policy", "process", "regulation", "general"]), title: localized, body: localized, sourceIds: z.array(z.string()), sortOrder: z.number().int(),
}).strict();

export const sourceSchema = z.object({
  id: z.string().min(1), kind: z.enum(["website", "file", "text"]), name: z.string().min(1), url: z.string().url().optional(), mimeType: z.string().optional(), bytes: z.number().nonnegative().optional(), contentHash: z.string().optional(), status: z.enum(["processing", "ready", "failed"]), summary: localized, updatedAt: z.date(),
}).strict();

const optionSchema = z.object({ id: z.string().min(1), label: localized, hint: localized.optional(), icon: z.string().optional() }).strict();
const questionBase = { id: z.string().min(1), title: localized, help: localized.optional(), required: z.boolean().optional() };
export const questionSchema = z.discriminatedUnion("kind", [
  z.object({ ...questionBase, kind: z.enum(["cards", "chips", "multi"]), options: z.array(optionSchema).min(1) }).strict(),
  z.object({ ...questionBase, kind: z.literal("slider"), min: z.number(), max: z.number() }).strict(),
]);

export const scenarioSchema = z.object({
  id: z.string().min(1), name: localized, description: localized, goal: localized, status: entityStatus, productIds: z.array(z.string()), serviceIds: z.array(z.string()), pricingDisplay: z.enum(["range", "from", "fixed", "hidden"]), handoffPolicy: z.enum(["request", "high-value", "low-confidence"]), steps: z.array(z.object({ id: z.string().min(1), title: localized, questions: z.array(questionSchema) }).strict()).min(1),
}).strict();

export const businessContextSchema = z.object({ content: z.string(), basedOnRevision: z.number().int().nonnegative(), updatedAt: z.date() }).strict();

export const businessTruthSchema = z.object({
  account: z.object({
    commercial: z.object({ type: z.enum(["standard", "design_partner"]), accessStatus: z.enum(["inactive", "active", "suspended"]) }).strict().nullable().optional(),
    management: z.object({ currentManagerUid: z.string().min(1).nullable().optional(), establishedAt: z.date().nullable().optional() }).strict().nullable().optional(),
    id: z.string().min(1), normalizedDomain: z.string(), websiteUrl: z.string(), onboardingStatus, qottaFit: z.boolean().nullable(), qottaFitReason: z.string(), revision: z.number().int().nonnegative(), editLock: z.object({ actorId: z.string(), leaseId: z.string(), acquiredAt: z.date(), heartbeatAt: z.date(), expiresAt: z.date() }).strict().nullable(), notificationPreferences: z.object({ leadEmail: z.string().optional(), emailAlerts: z.boolean(), dailyDigest: z.boolean(), handoffAlerts: z.boolean() }).strict(), status: entityStatus, isDemo: z.boolean(), createdAt: z.date(), updatedAt: z.date(),
  }).strict(),
  business: businessProfileSchema,
  products: z.array(productSchema),
  services: z.array(productSchema),
  knowledge: z.array(knowledgeSchema),
  scenarios: z.array(scenarioSchema),
  sources: z.array(sourceSchema),
  context: businessContextSchema,
}).strict();
